import { useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock3, Edit2, RefreshCw } from 'lucide-react';
import { useToast } from '../../Common/context/ToastContext';
import AdminOtpActionModal from '../../Common/components/common/AdminOtpActionModal';
import { ADMIN_SINGLE_CP_MODE } from '../../Common/config/menuConfig';
import {
  refreshCpPayoutAml,
  registerCpPayoutProfile,
  saveCpPayoutProfile,
} from '../api/cpPayout';

export const PAYOUT_REGISTRATION_LABELS = {
  INCOMPLETE: '필수정보 미완성',
  PENDING: '등록 대기',
  PROCESSING: '등록 처리 중',
  REGISTERED: '하위몰 등록 완료',
  UPDATE_REQUIRED: '정보 변경 재등록 필요',
  FAILED: '등록 실패',
};

export const PAYOUT_AML_LABELS = {
  UNKNOWN: '확인 전',
  PENDING: '심사 대기',
  IN_PROGRESS: '심사 중',
  APPROVED: '승인',
  HOLD: '보류',
  EXPIRED: '만료',
};

const MISSING_FIELD_LABELS = {
  businessNo: '사업자번호',
  companyName: '회사명',
  ownerName: '대표자명',
  bankCode: '은행코드',
  bankAccountNumber: '계좌번호',
  bankAccountHolder: '예금주',
  phone: '연락처',
  email: '이메일',
  representativeBirthDate: '대표자 생년월일',
};

const KG_ERROR_GUIDES = {
  KG_INICIS_PAYOUT_DISABLED: 'KG이니시스 지급대행 기능이 서버에서 비활성화되어 있습니다.',
  KG_INICIS_PAYOUT_CREDENTIALS_REQUIRED: 'KG이니시스 지급대행 인증정보를 확인해 주세요.',
  KG_INICIS_PAYOUT_SUBMALL_NOT_REGISTERED: '하위몰 등록이 완료되지 않아 AML 상태를 조회할 수 없습니다.',
  KG_INICIS_PAYOUT_REGISTRATION_FAILED: 'KG이니시스 하위몰 등록에 실패했습니다.',
  KG_INICIS_PAYOUT_AML_FAILED: 'KG이니시스 AML 상태조회에 실패했습니다.',
};

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function toBoolean(value, fallback = false) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'y'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'n', ''].includes(normalized)) return false;
  }
  return fallback;
}

function upper(value, fallback = '') {
  return String(value || fallback).trim().toUpperCase();
}

function normalizeMissingFields(value) {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (!value) return [];
  return String(value).split(/[,|]/).map((item) => item.trim()).filter(Boolean);
}

export function normalizePayoutCapability(value = {}) {
  const data = value?.payoutCapabilities ?? value?.payout_capabilities ?? value;
  return {
    provider: upper(data.provider, 'KG_INICIS'),
    enabled: toBoolean(data.enabled),
    sellerOnboardingRequired: toBoolean(data.sellerOnboardingRequired ?? data.seller_onboarding_required),
    amlManagementEnabled: toBoolean(data.amlManagementEnabled ?? data.aml_management_enabled),
    balanceCheckEnabled: toBoolean(data.balanceCheckEnabled ?? data.balance_check_enabled),
    onboardingBatchEnabled: toBoolean(data.onboardingBatchEnabled ?? data.onboarding_batch_enabled),
    otpRequired: toBoolean(data.otpRequired ?? data.otp_required, !ADMIN_SINGLE_CP_MODE),
  };
}

export function normalizePayoutProfile(value = {}) {
  const profile = value?.profile ?? value;
  const cpId = Number(firstValue(profile.cpId, profile.cp_id, profile.id));
  const registrationStatus = upper(firstValue(profile.registrationStatus, profile.registration_status), 'INCOMPLETE');
  const amlStatus = upper(firstValue(profile.amlStatus, profile.aml_status), 'UNKNOWN');
  const amlExpired = toBoolean(profile.amlExpired ?? profile.aml_expired);
  const serverPayoutEligible = toBoolean(profile.payoutEligible ?? profile.payout_eligible ?? profile.payoutEnabled ?? profile.payout_enabled);
  return {
    cpId: Number.isInteger(cpId) && cpId > 0 ? cpId : null,
    cpLoginId: firstValue(profile.cpLoginId, profile.cp_login_id, '-'),
    cpName: firstValue(profile.cpName, profile.cp_name, profile.name, '-'),
    cpStatus: upper(firstValue(profile.cpStatus, profile.cp_status)),
    provider: upper(profile.provider, 'KG_INICIS'),
    externalMallId: firstValue(profile.externalMallId, profile.external_mall_id, '-'),
    registrationStatus,
    registrationCode: firstValue(profile.registrationCode, profile.registration_code, '-'),
    registrationMessage: firstValue(profile.registrationMessage, profile.registration_message, '-'),
    amlStatus,
    amlExpired,
    amlValidFrom: firstValue(profile.amlValidFrom, profile.aml_valid_from),
    amlValidTo: firstValue(profile.amlValidTo, profile.aml_valid_to),
    amlCheckedAt: firstValue(profile.amlCheckedAt, profile.aml_checked_at),
    payoutEligible: registrationStatus === 'REGISTERED' && amlStatus === 'APPROVED' && !amlExpired && serverPayoutEligible,
    representativeBirthDateConfigured: toBoolean(profile.representativeBirthDateConfigured ?? profile.representative_birth_date_configured),
    missingFields: normalizeMissingFields(profile.missingFields ?? profile.missing_fields),
    attemptCount: Number(firstValue(profile.attemptCount, profile.attempt_count, 0)) || 0,
    nextRetryAt: firstValue(profile.nextRetryAt, profile.next_retry_at),
    lastError: firstValue(profile.lastError, profile.last_error, ''),
    updatedAt: firstValue(profile.updatedAt, profile.updated_at),
    isNew: toBoolean(profile.isNew ?? profile.is_new),
  };
}

export function payoutRegistrationTone(status) {
  if (status === 'REGISTERED') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (status === 'PROCESSING' || status === 'PENDING') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (status === 'FAILED') return 'border-red-200 bg-red-50 text-red-700';
  if (status === 'UPDATE_REQUIRED') return 'border-amber-200 bg-amber-50 text-amber-700';
  return 'border-slate-200 bg-slate-100 text-slate-600';
}

export function payoutAmlTone(status, expired) {
  if (expired || status === 'EXPIRED') return 'border-red-200 bg-red-50 text-red-700';
  if (status === 'APPROVED') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (status === 'PENDING' || status === 'IN_PROGRESS') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (status === 'HOLD') return 'border-amber-200 bg-amber-50 text-amber-700';
  return 'border-slate-200 bg-slate-100 text-slate-600';
}

function formatKst(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(date);
}

function formatDateOnly(value) {
  if (!value) return '-';
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value).replaceAll('-', '.');
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('ko-KR', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

function readableError(errorOrMessage) {
  const message = String(errorOrMessage?.message ?? errorOrMessage ?? '').trim();
  const code = Object.keys(KG_ERROR_GUIDES).find((key) => message.includes(key));
  if (!code) return !message ? '요청 처리에 실패했습니다.' : (message.length > 300 ? `${message.slice(0, 300)}…` : message);
  const detail = message.length > 220 ? `${message.slice(0, 220)}…` : message;
  return `${KG_ERROR_GUIDES[code]}\n상세: ${detail}`;
}

function validateBirthDate(value) {
  if (!/^\d{8}$/.test(value)) return 'YYYYMMDD 형식의 숫자 8자리를 입력해 주세요.';
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(4, 6));
  const day = Number(value.slice(6, 8));
  if (year < 1 || month < 1 || month > 12) return '실제 날짜를 입력해 주세요.';
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (day < 1 || day > days[month - 1]) return '실제 날짜를 입력해 주세요.';
  const today = new Date();
  const todayKey = Number(`${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`);
  return Number(value) > todayKey ? '미래 날짜는 입력할 수 없습니다.' : '';
}

export default function SingleCpPayoutPanel({ capability, profile, loading, error, onReload, onEditCp, compact = false }) {
  const { showToast } = useToast();
  const workingRef = useRef(false);
  const [birthDate, setBirthDate] = useState('');
  const [birthDateError, setBirthDateError] = useState('');
  const [workingAction, setWorkingAction] = useState('');
  const [confirmRegister, setConfirmRegister] = useState(false);
  const [pendingAction, setPendingAction] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');

  const actionBusy = Boolean(workingAction);

  const executeAction = async (action, otpToken) => {
    if (!profile?.cpId || workingRef.current) return;
    workingRef.current = true;
    setWorkingAction(action);
    setOtpError('');
    try {
      let data;
      if (action === 'birthDate') {
        data = await saveCpPayoutProfile({ cpId: profile.cpId, representativeBirthDate: birthDate, otpToken });
      } else if (action === 'register') {
        data = await registerCpPayoutProfile({ cpId: profile.cpId, otpToken });
      } else {
        data = await refreshCpPayoutAml({ cpId: profile.cpId, otpToken });
      }

      if (action === 'birthDate') {
        setBirthDate('');
        setBirthDateError('');
        showToast('대표자 생년월일 등록 여부가 갱신되었습니다.');
      } else if (action === 'register') {
        const result = data?.profile ? normalizePayoutProfile(data) : null;
        if (!result || result.missingFields.length > 0 || result.registrationStatus !== 'REGISTERED') {
          showToast('등록 요청 결과를 반영했습니다. 현재 등록상태와 누락 정보를 확인해 주세요.', 'error');
        } else if (result.payoutEligible) {
          showToast('하위몰 등록과 AML 승인이 확인되어 지급 가능한 상태입니다.');
        } else {
          const amlLabel = PAYOUT_AML_LABELS[result.amlStatus] || result.amlStatus;
          showToast('하위몰 등록이 완료되었습니다. AML ' + amlLabel + ' 상태이며 현재는 지급 불가입니다.');
        }
      } else {
        const result = data?.profile ? normalizePayoutProfile(data) : null;
        if (result?.payoutEligible) {
          showToast('AML 승인과 지급 가능 상태를 확인했습니다.');
        } else {
          const amlLabel = result ? (PAYOUT_AML_LABELS[result.amlStatus] || result.amlStatus) : '갱신';
          showToast('AML ' + amlLabel + ' 상태를 반영했습니다. 현재는 지급 불가입니다.');
        }
      }
      setPendingAction('');
      setOtp('');
      await onReload?.();
    } catch (requestError) {
      const message = readableError(requestError);
      if (capability?.otpRequired && [4001, 4002, 4003].includes(Number(requestError?.code))) {
        setOtpError(message);
      } else {
        setPendingAction('');
        setOtp('');
        showToast(message, 'error');
      }
    } finally {
      workingRef.current = false;
      setWorkingAction('');
    }
  };

  const beginAction = (action) => {
    if (workingRef.current) return;
    if (capability?.otpRequired) {
      setPendingAction(action);
      setOtp('');
      setOtpError('');
    } else {
      executeAction(action);
    }
  };

  const submitBirthDate = () => {
    const validationError = validateBirthDate(birthDate);
    setBirthDateError(validationError);
    if (!validationError) beginAction('birthDate');
  };

  if (loading) return compact ? null : <EmptyState>KG이니시스 지급 프로필을 불러오는 중입니다.</EmptyState>;
  if (error) return <Notice tone="error">{error}</Notice>;
  if (!capability?.enabled) return <Notice tone="warning">KG이니시스 지급대행 기능이 비활성화되어 있습니다.</Notice>;
  if (!profile) return <EmptyState>등록된 CP 지급 프로필이 없습니다.</EmptyState>;

  return (
    <div className={compact ? 'mt-5 border-t border-[#e5e8eb] pt-5' : 'h-full overflow-y-auto bg-[#f7f8fa] p-5'}>
      <div className={compact ? 'space-y-4' : 'mx-auto max-w-[1150px] space-y-4'}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div><h2 className={(compact ? 'text-[16px]' : 'text-[20px]') + ' font-bold text-[#191f28]'}>{compact ? 'KG이니시스 지급·AML' : '판매자 등록 및 AML 관리'}</h2><p className="mt-1 text-[12px] text-[#6b7684]">CP 자체의 하위몰 등록과 AML 상태를 관리합니다.</p></div>
          <button type="button" disabled={loading || actionBusy} onClick={onReload} className="flex items-center gap-2 rounded-lg border border-[#dfe3e8] bg-white px-3 py-2 text-[13px] font-semibold disabled:opacity-40"><RefreshCw size={15} className={loading ? 'animate-spin' : ''} />상태 새로고침</button>
        </div>

        <Notice>KG이니시스 지급을 받는 대상은 가맹점(CP)입니다.<br />하위몰 등록과 AML 승인이 완료된 CP만 향후 지급지시가 가능합니다.<br />현재 실제 지급지시 기능은 준비 중입니다.</Notice>

        {!capability.onboardingBatchEnabled && <Notice>자동 일괄 등록을 사용하지 않습니다. CP 정보를 검토한 후 관리자가 직접 등록해 주세요.</Notice>}
        {profile.isNew && <Notice tone="warning">아직 지급 프로필이 생성되지 않았습니다. CP 필수정보를 확인하고 대표자 생년월일을 저장해 초기 등록을 진행해 주세요.</Notice>}


        <section className="rounded-xl border border-[#e5e8eb] bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><p className="text-[12px] font-semibold text-[#8b95a1]">KG이니시스 지급대상 CP</p><h3 className="mt-1 text-[19px] font-bold text-[#191f28]">{profile.cpName} <span className="font-mono text-[13px] font-medium text-[#8b95a1]">({profile.cpLoginId})</span></h3></div>
            <EligibilityBadge enabled={profile.payoutEligible} />
          </div>
          <div className={'mt-5 grid grid-cols-2 gap-x-6 gap-y-4 ' + (compact ? '' : 'lg:grid-cols-4')}>
            <Detail label="Sub Mall ID" value={profile.externalMallId} mono />
            <Detail label="판매자 등록상태" value={<Badge className={payoutRegistrationTone(profile.registrationStatus)}>{PAYOUT_REGISTRATION_LABELS[profile.registrationStatus] || profile.registrationStatus}</Badge>} />
            <Detail label="AML 상태" value={<Badge className={payoutAmlTone(profile.amlStatus, profile.amlExpired)}>{profile.amlExpired ? '만료' : (PAYOUT_AML_LABELS[profile.amlStatus] || profile.amlStatus)}</Badge>} />
            <Detail label="AML 유효기간" value={`${formatDateOnly(profile.amlValidFrom)} ~ ${formatDateOnly(profile.amlValidTo)}`} />
            <Detail label="등록 응답" value={`${profile.registrationCode} · ${profile.registrationMessage}`} />
            <Detail label="최근 AML 확인" value={formatKst(profile.amlCheckedAt)} />
            <Detail label="등록 시도 횟수" value={`${profile.attemptCount}회`} />
            <Detail label="다음 재시도 예정일" value={formatKst(profile.nextRetryAt)} />
            <Detail label="최근 갱신" value={formatKst(profile.updatedAt)} />
          </div>
        </section>

        <section className="rounded-xl border border-[#e5e8eb] bg-white p-5">
          <div className="flex items-center justify-between gap-3"><h3 className="font-bold text-[#191f28]">필수정보 및 대표자 확인</h3><button type="button" onClick={onEditCp} className="flex items-center gap-1.5 rounded-lg border border-[#dfe3e8] px-3 py-2 text-[12px] font-semibold text-[#4e5968]"><Edit2 size={14} />CP 기본정보 수정</button></div>
          {profile.missingFields.length ? <div className="mt-3 flex flex-wrap gap-2">{profile.missingFields.map((field) => <Badge key={field} className="border-amber-200 bg-amber-50 text-amber-700">{MISSING_FIELD_LABELS[field] || field}</Badge>)}</div> : <p className="mt-3 flex items-center gap-2 text-[13px] text-emerald-700"><CheckCircle2 size={16} />확인된 누락 필드가 없습니다.</p>}
          <p className="mt-4 rounded-lg bg-[#f8faf9] p-3 text-[13px] leading-6 text-[#4e5968]">대표자 생년월일은 아래에서 입력할 수 있습니다. 사업자번호, 대표자명, 계좌정보, 연락처, 이메일은 CP 기본정보에서 수정해 주세요.</p>
          <div className={'mt-4 grid gap-3 ' + (compact ? '' : 'lg:grid-cols-[180px_minmax(0,1fr)_150px] lg:items-end')}>
            <Detail label="생년월일 등록 여부" value={profile.representativeBirthDateConfigured ? '등록됨' : '미등록'} />
            <label className="text-[12px] font-semibold text-[#6b7684]">대표자 생년월일<input type="password" inputMode="numeric" autoComplete="new-password" value={birthDate} onChange={(event) => { setBirthDate(event.target.value.replace(/\D/g, '').slice(0, 8)); setBirthDateError(''); }} onKeyDown={(event) => { if (event.key === 'Enter') submitBirthDate(); }} placeholder="YYYYMMDD" maxLength={8} className="mt-1 h-[42px] w-full rounded-lg border border-[#dfe3e8] px-3 font-mono tracking-wider outline-none focus:border-[var(--brand-primary)]" /></label>
            <button type="button" disabled={actionBusy || birthDate.length !== 8} onClick={submitBirthDate} className="h-[42px] rounded-lg bg-[#191f28] px-4 text-[13px] font-bold text-white disabled:opacity-40">{workingAction === 'birthDate' ? '저장 중...' : '안전하게 저장'}</button>
          </div>
          {birthDateError && <p className="mt-2 text-[12px] text-red-600">{birthDateError}</p>}
        </section>

        {profile.lastError && <Notice tone="error"><strong>최근 연동 오류</strong>{'\n'}{readableError(profile.lastError)}</Notice>}

        <section className="rounded-xl border border-[#e5e8eb] bg-white p-5">
          <h3 className="font-bold text-[#191f28]">KG이니시스 관리 작업</h3>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <button type="button" disabled={actionBusy || profile.registrationStatus === 'PROCESSING'} onClick={() => setConfirmRegister(true)} className="rounded-lg bg-[var(--brand-primary)] px-4 py-3 text-[14px] font-bold text-white disabled:opacity-40">{workingAction === 'register' ? '등록 처리 중...' : (['REGISTERED', 'UPDATE_REQUIRED'].includes(profile.registrationStatus) ? '변경정보 재등록' : '하위몰 등록')}</button>
            <button type="button" disabled={actionBusy || profile.registrationStatus !== 'REGISTERED' || !capability.amlManagementEnabled} onClick={() => beginAction('aml')} className="rounded-lg border border-[var(--brand-primary)] px-4 py-3 text-[14px] font-bold text-[var(--brand-primary)] disabled:opacity-40">{workingAction === 'aml' ? 'AML 확인 중...' : 'AML 상태 새로고침'}</button>
          </div>
          {profile.registrationStatus !== 'REGISTERED' && <p className="mt-2 text-[12px] text-[#8b95a1]">AML 상태는 하위몰 등록 완료 후 조회할 수 있습니다.</p>}
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] leading-5 text-amber-900"><Clock3 size={16} className="mt-0.5 shrink-0" />KG이니시스 실제 지급지시 기능은 준비 중입니다. 송금 또는 지급 완료 처리는 실행하지 않습니다.</div>
        </section>
      </div>

      {confirmRegister && <ConfirmModal working={actionBusy} onClose={() => { if (!actionBusy) setConfirmRegister(false); }} onConfirm={() => { setConfirmRegister(false); beginAction('register'); }} />}
      <AdminOtpActionModal open={Boolean(pendingAction)} title={pendingAction === 'register' ? '하위몰 등록 OTP 확인' : pendingAction === 'aml' ? 'AML 상태조회 OTP 확인' : '지급정보 저장 OTP 확인'} value={otp} error={otpError} working={actionBusy} onChange={setOtp} onClose={() => { if (!actionBusy) { setPendingAction(''); setOtp(''); setOtpError(''); } }} onSubmit={() => { if (!/^\d{6}$/.test(otp)) { setOtpError('현재 OTP 6자리를 입력해 주세요.'); return; } executeAction(pendingAction, otp); }} />
    </div>
  );
}

function Badge({ children, className = '' }) { return <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${className}`}>{children}</span>; }
function EligibilityBadge({ enabled }) { return <Badge className={enabled ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'}>{enabled ? '지급 가능' : '지급 불가'}</Badge>; }
function Detail({ label, value, mono = false }) { return <div className="min-w-0"><p className="text-[11px] font-semibold text-[#8b95a1]">{label}</p><div className={`mt-1 break-words text-[13px] font-semibold text-[#333d4b] ${mono ? 'font-mono' : ''}`}>{value || '-'}</div></div>; }
function EmptyState({ children }) { return <div className="flex h-full items-center justify-center bg-[#f7f8fa] p-8 text-[14px] text-[#8b95a1]">{children}</div>; }
function Notice({ children, tone = 'info' }) { const style = tone === 'error' ? 'border-red-200 bg-red-50 text-red-800' : tone === 'warning' ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-slate-200 bg-white text-[#4e5968]'; return <div className={`whitespace-pre-line rounded-xl border p-4 text-[13px] leading-6 ${style}`}><div className="flex items-start gap-2"><AlertTriangle size={16} className="mt-1 shrink-0" /><span>{children}</span></div></div>; }
function ConfirmModal({ working, onClose, onConfirm }) { return <div className="fixed inset-0 z-[3900] flex items-center justify-center bg-black/45 p-4" onClick={onClose}><div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={(event) => event.stopPropagation()}><h2 className="text-lg font-bold text-[#191f28]">KG이니시스 하위몰 등록</h2><p className="mt-3 text-sm leading-6 text-[#4e5968]">입력된 가맹점 정보를 KG이니시스 운영 서버에 전송합니다.<br />계속하시겠습니까?</p><div className="mt-5 flex gap-2"><button type="button" disabled={working} onClick={onClose} className="flex-1 rounded-xl border border-[#dfe3e8] py-3 font-semibold disabled:opacity-40">취소</button><button type="button" disabled={working} onClick={onConfirm} className="flex-1 rounded-xl bg-[#191f28] py-3 font-semibold text-white disabled:opacity-40">계속</button></div></div></div>; }
